import { Tab, Tabs, useTheme } from '@mui/material'
import React from 'react'
import { Link, matchPath, type PathMatch, useLocation } from 'react-router-dom'
import { isUsingProdDataRoutes } from '../../utils/api/servers'
import { isEmptyString } from '../../utils/string-utils'

function useRouteMatch (patterns: readonly string[]): PathMatch | null {
  const { pathname } = useLocation()

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i]
    const possibleMatch = matchPath(pattern, pathname)
    if (possibleMatch !== null) {
      return possibleMatch
    }
  }

  return null
}

function redirectUnmappedRoutes (path: string | undefined): string | boolean {
  if (isEmptyString(path) || path === '/' || path === '/callback') {
    return false
  } else {
    return path as string
  }
}

const NavigationBar: React.FunctionComponent = () => {
  const theme = useTheme()

  // You need to provide the routes in descendant order.
  // This means that if you have nested routes like:
  // users, users/new, users/edit.
  // Then the order should be ['users/add', 'users/edit', 'users'].
  const routeMatch = useRouteMatch([
    '/strategy',
    '/strategy_status',
    '/simple_strategy_1',
    '/strategy_1',
    '/reconciliation',
    '/transfer_tabs',
    '/withdraw',
    '/balance_plus',
    '/exposure_statistic',
    '/exposure',
    '/balance',
    '/pnl',
    '/risk',
    '/funding_rate',
    '/user',
    '/',
  ])
  const currentTab = redirectUnmappedRoutes(routeMatch?.pattern?.path)
  const useProdDataRoutes = isUsingProdDataRoutes()

  return (
    <Tabs
      value={currentTab}
      variant='scrollable'
      scrollButtons
      allowScrollButtonsMobile
      TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan'/> }}
      sx={{
        '& .MuiTabs-flexContainer': {
          lg: {
            justifyContent: 'center',
          }
        },
        '& .MuiTabs-scrollButtons.Mui-disabled': {
          opacity: 0.3,
        },
        '& .MuiTabs-indicator': {
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'transparent',
        },
        '& .MuiTabs-indicatorSpan': {
          maxWidth: 40,
          width: '100%',
          backgroundColor: theme.palette.primary.main,
        },
      }}
    >
      <Tab disableRipple label='user' value='/user' to='/user' component={Link}/>

      <Tab disableRipple label='funding' value='/funding_rate' to='/funding_rate' component={Link}/>
      <Tab disableRipple label='risk' value='/risk' to='/risk' component={Link}/>
      <Tab disableRipple label='balance' value='/balance' to='/balance' component={Link}/>
      <Tab disableRipple label='pnl' value='/pnl' to='/pnl' component={Link}/>
      <Tab disableRipple label='exposure' value='/exposure' to='/exposure' component={Link}/>
      <Tab disableRipple label='expo. stat.' value='/exposure_statistic' to='/exposure_statistic' component={Link}/>
      <Tab disableRipple label='balance+' value='/balance_plus' to='/balance_plus' component={Link}/>

      {
        useProdDataRoutes && <Tab disableRipple label='withdraw' value='/withdraw' to='/withdraw' component={Link}/>
      }

      {
          useProdDataRoutes && <Tab disableRipple label='transfers' value='/transfer_tabs' to='/transfer_tabs' component={Link}/>
      }

      {
        useProdDataRoutes && <Tab disableRipple label='reconciliation' value='/reconciliation' to='/reconciliation' component={Link}/>
      }

      {
        useProdDataRoutes && <Tab disableRipple label='status' value='/strategy_status' to='/strategy_status' component={Link}/>
      }

      {
        useProdDataRoutes && <Tab disableRipple label='strategy' value='/strategy' to='/strategy' component={Link}/>
      }

      {
        !useProdDataRoutes && <Tab disableRipple label='local strategy (simple)' value='/simple_strategy_1' to='/simple_strategy_1' component={Link}/>
      }

      {
        !useProdDataRoutes && <Tab disableRipple label='local strategy (full)' value='/strategy_1' to='/strategy_1' component={Link}/>
      }
    </Tabs>
  )
}

export default NavigationBar
